import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../api/axios';
import { SyncManager } from '../utils/SyncManager';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    // Trigger pre-fetch when user logs in or loads
    useEffect(() => {
        if (user) {
            SyncManager.prefetchCriticalData();
        }
    }, [user]);

    useEffect(() => {
        // Check for existing session
        const token = localStorage.getItem('token');
        const savedUser = localStorage.getItem('user');

        if (token && savedUser) {
            try {
                setUser(JSON.parse(savedUser));
                // Verify token is still valid
                api.get('/auth/me')
                    .then(response => {
                        setUser(response.data);
                        localStorage.setItem('user', JSON.stringify(response.data));
                    })
                    .catch((error) => {
                        // If offline, assume token is valid and keep user
                        if (!navigator.onLine || error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
                            console.warn('Offline: Skipping token validation');
                            // User is already set from localStorage above
                        } else {
                            logout();
                        }
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            } catch {
                logout();
                setLoading(false);
            }
        } else {
            setLoading(false);
        }
    }, []);

    const login = async (email, password) => {
        const response = await api.post('/auth/login', { email, password });
        const { token, user: userData } = response.data;

        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);

        return userData;
    };

    const logout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
    };

    const updateProfile = async (data) => {
        await api.put('/auth/profile', data);
        const updatedUser = { ...user, ...data };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setUser(updatedUser);
    };

    const changePassword = async (currentPassword, newPassword) => {
        await api.put('/auth/password', { currentPassword, newPassword });
    };

    const hasPermission = (permission, action = 'view') => {
        if (!user) return false;
        if (user.role === 'super_admin' || user.role === 'branch_admin') return true;
        return user.permissions?.[permission]?.[action] || false;
    };

    const value = {
        user,
        loading,
        login,
        logout,
        updateProfile,
        changePassword,
        hasPermission,
        isAdmin: user?.role === 'super_admin' || user?.role === 'branch_admin',
        isSuperAdmin: user?.role === 'super_admin',
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
